/**
 * Anchors tutorial click markers to the design rather than to the viewport.
 *
 * The Stitch Editor pans and zooms, and the canvas element fills whatever space
 * the window gives it, so a marker placed at "52% across the canvas" only lands
 * correctly at the exact window size and zoom it was recorded at. Everything the
 * learner is asked to click is really a point in the design's own coordinate
 * space — the SVG viewBox the app already stores rails in — so markers are
 * defined there and projected to the screen through the live transform.
 */

const DESIGN_SVG = '[data-pan-zoom="design"]';
const CANVAS_SELECTOR = '[data-tutorial="stitch-canvas"]';
const RAIL_PATH_SELECTOR = '[data-tutorial="satin-selectable-path"]';
const SPLIT_SHAPE_SELECTOR = '[data-tutorial="satin-split-shape"]';

// How many spots around a path to try before giving up on placing its marker.
const CANDIDATES = 24;

// The spot most recently found to be reachable, so the common case is a single
// cheap projection instead of a fresh hit-test sweep on every frame.
const settled = new Map();

/** Maps a point in SVG user space to viewport pixels, or null if unavailable. */
const project = (svg, x, y) => {
  if (!svg || typeof svg.createSVGPoint !== 'function') return null;

  const matrix = svg.getScreenCTM();
  if (!matrix) return null;

  const point = svg.createSVGPoint();
  point.x = x;
  point.y = y;

  const { x: left, y: top } = point.matrixTransform(matrix);
  return { left, top };
};

/** Projects design-space coordinates using the design layer's own transform. */
export const projectDesignPoint = ({ x, y }) => project(document.querySelector(DESIGN_SVG), x, y);

/**
 * A point sitting exactly on one of the selectable satin paths.
 *
 * Choosing satin columns is a path pick, not a free click — the app only
 * responds to a hit on one of these strokes — so the marker is placed on the
 * geometry itself instead of near where it was once seen.
 *
 * `at` is a fraction of the path's length, letting a step choose a spot on the
 * shape that reads clearly rather than wherever the path happens to start.
 */
export const projectRailPoint = ({ index, at = 0.25 }, { search = false } = {}) => {
  const group = document.querySelector(`${RAIL_PATH_SELECTOR}[data-path-index="${index}"]`);
  const path = group?.querySelector('path');

  if (!path || typeof path.getTotalLength !== 'function') return null;

  const length = path.getTotalLength();
  if (!length) return null;

  const key = `${index}:${at}`;
  const spotAt = (fraction) => {
    const point = path.getPointAtLength(length * fraction);
    return project(path.ownerSVGElement, point.x, point.y);
  };

  if (!search) {
    const known = settled.get(key);
    return known == null ? null : spotAt(known);
  }

  // A point can be on the path and still be unreachable — the dialogue, a side
  // panel or a closing dialog may sit over it. Walking around the shape from
  // the preferred spot and hit-testing each candidate means the marker only
  // ever appears somewhere a click genuinely arrives, whatever else is on
  // screen. No reachable spot means no marker, which reads as "not yet".
  for (let i = 0; i < CANDIDATES; i += 1) {
    const fraction = (at + i / CANDIDATES) % 1;
    const spot = spotAt(fraction);
    const hit = spot && document.elementFromPoint(spot.left, spot.top);

    if (hit && typeof hit.closest === 'function' && hit.closest(RAIL_PATH_SELECTOR) === group) {
      settled.set(key, fraction);
      return spot;
    }
  }

  settled.delete(key);
  return null;
};

// The ends of each shape, in the design's own coordinates. Where they are on
// the shape never changes, so this is measured once and only the projection to
// the screen is redone as the learner pans and zooms.
const ends = new Map();

/** Evenly spaced points along a path, in its own user space. */
const walkPath = (path, samples) => {
  const length = path.getTotalLength();
  if (!length) return [];

  const points = [];
  for (let i = 0; i < samples; i += 1) {
    points.push(path.getPointAtLength((length * i) / samples));
  }

  return points;
};

const distance = (a, b) => Math.hypot(a.x - b.x, a.y - b.y);

/**
 * The middle of the shape's end, rather than the corner that reaches furthest.
 *
 * The furthest-apart pair lands on a corner of the tip — on the smile that is
 * out at the side of the end rather than across it, which reads as a cut that
 * misses. The outline crosses the end as one unbroken run of samples, so the
 * middle of that run is the middle of the end, whatever shape the tip is.
 */
const middleOfEnd = (points, index, other) => {
  const end = points[index];
  const reach = distance(end, other) * 0.1;
  const at = (i) => points[((i % points.length) + points.length) % points.length];
  const near = (i) => distance(at(i), end) <= reach;

  let first = index;
  let last = index;

  while (near(first - 1) && last - first < points.length - 1) first -= 1;
  while (near(last + 1) && last - first < points.length - 1) last += 1;

  return at(Math.round((first + last) / 2));
};

/**
 * The two points furthest apart on a shape being split into a satin column.
 *
 * Splitting cuts a solid shape in two down its length, and the halves become
 * the column's edges — so the cut has to run the long way for the stitches to
 * end up lying across the narrow way. On an eye that's top to bottom, on a
 * smile it's tip to tip, and "furthest apart" says so without either shape
 * having to be described in coordinates.
 */
const splitEndsOf = (index) => {
  const key = String(index);

  const shape = document.querySelector(`${SPLIT_SHAPE_SELECTOR}[data-subpath-index="${key}"]`);
  if (!shape || typeof shape.getTotalLength !== 'function') {
    ends.delete(key);
    return null;
  }

  const d = shape.getAttribute('d');
  const known = ends.get(key);

  if (!known || known.d !== d) {
    const points = walkPath(shape, 72);
    if (points.length < 4) return null;

    let best = null;
    let longest = -1;

    for (let i = 0; i < points.length; i += 1) {
      for (let k = i + 1; k < points.length; k += 1) {
        const span = (points[i].x - points[k].x) ** 2 + (points[i].y - points[k].y) ** 2;

        if (span > longest) {
          longest = span;
          best = [i, k];
        }
      }
    }

    if (!best) return null;

    const [from, to] = best;
    ends.set(key, {
      d,
      points: [
        middleOfEnd(points, from, points[to]),
        middleOfEnd(points, to, points[from]),
      ],
    });
  }

  return ends
    .get(key)
    .points.map((point) => project(shape.ownerSVGElement, point.x, point.y));
};

/** Whether a click at this spot would reach the canvas at all. */
const onCanvas = (spot) => {
  const hit = spot && document.elementFromPoint(spot.left, spot.top);
  return !!hit && typeof hit.closest === 'function' && !!hit.closest(CANVAS_SELECTOR);
};

// Which of a step's design points were last found to be clickable.
const usable = new Map();

/** Required cuts keep their authored order even when a panel covers a point.
 * The dialogue moves away from the active pair; occlusion must never remove a
 * cut or shorten the click count, leaving the design partly connected.
 */
const reachableDesignPoints = (stepData) => {
  const spots = stepData.designPoints.map(projectDesignPoint);
  return spots.every(Boolean) ? spots : null;
};

/**
 * Both ends of every shape the step asks the learner to cut across, as one
 * flat list of clicks. A shape whose ends aren't both reachable is dropped
 * whole, the same way a guide line is — half a cut splits nothing.
 */
const resolveSplitShapes = (stepData, search) => {
  const cuts = stepData.splitShapes.map(({ subpath }) => splitEndsOf(subpath));
  if (cuts.some((cut) => !cut || cut.some((spot) => !spot))) return null;

  const key = stepData.id;

  if (search) {
    const keep = cuts.reduce((found, cut, i) => (cut.every(onCanvas) ? [...found, i] : found), []);

    if (keep.length) usable.set(key, keep);
    else usable.delete(key);
  }

  const keep = usable.get(key);
  return keep ? keep.flatMap((i) => cuts[i]) : null;
};

// Guide lines last found to be drawable, so the sweep below runs on the slow
// timer rather than every frame.
const guides = new Map();

/** Evenly spaced points along a path, in its own user space. */
const samplePath = (path, samples) => {
  const length = path.getTotalLength();
  const points = [];

  for (let i = 0; i <= samples; i += 1) {
    points.push(path.getPointAtLength((length * i) / samples));
  }

  return points;
};

/** The sampled point closest to `target`. */
const nearest = (points, target) => {
  let best = null;
  let bestDistance = Infinity;

  for (const candidate of points) {
    const distance = (candidate.x - target.x) ** 2 + (candidate.y - target.y) ** 2;

    if (distance < bestDistance) {
      bestDistance = distance;
      best = candidate;
    }
  }

  return best;
};

/**
 * Guide lines across a finished satin column, as start/end pairs.
 *
 * A guide runs from one rail to the other, so both ends are read off the rails
 * the learner just created rather than from coordinates recorded on one screen.
 * Candidates are spread along the column and only kept when both ends are
 * clickable, which keeps the guides away from the side panel and the dialogue
 * whatever size the window is.
 */
const resolveGuideRungs = (stepData, search) => {
  const { pair = 0, count = 4 } = stepData.guideRungs;
  const key = `${stepData.id}:${pair}`;

  if (!search) return guides.get(key) || null;

  const railOf = (index) =>
    document.querySelector(`[data-tutorial="satin-rail"][data-rail-pair="${pair}"][data-rail-index="${index}"]`);

  const from = railOf(0);
  const to = railOf(1);

  if (!from?.getTotalLength || !to?.getTotalLength) {
    guides.delete(key);
    return null;
  }

  const length = from.getTotalLength();
  if (!length) {
    guides.delete(key);
    return null;
  }

  // Several times more candidates than guides wanted, so there is room to drop
  // the ones that land somewhere unreachable and still finish well spread out.
  const ATTEMPTS = count * 6;
  const opposite = samplePath(to, 160);
  const pool = [];

  for (let i = 0; i < ATTEMPTS; i += 1) {
    const start = from.getPointAtLength((length * (i + 0.5)) / ATTEMPTS);
    const end = nearest(opposite, start);
    if (!end) continue;

    const a = project(from.ownerSVGElement, start.x, start.y);
    const b = project(to.ownerSVGElement, end.x, end.y);

    if (a && b && onCanvas(a) && onCanvas(b)) pool.push([a, b]);
  }

  if (!pool.length) {
    guides.delete(key);
    return null;
  }

  // Spread the guides across whatever stretch of the column is usable instead
  // of bunching them at the end the sweep happened to start from.
  //
  // A ring's two ends are the same place, so spacing them across the candidate
  // list inclusive of both would put the first and last guide on top of each
  // other and leave a quarter of the circle bare. A closed rail therefore gets
  // the last position dropped; an open one keeps it, or its far end would
  // never get a guide.
  const start = from.getPointAtLength(0);
  const finish = from.getPointAtLength(length);
  const closed = Math.hypot(finish.x - start.x, finish.y - start.y) < length * 0.01;

  const take = Math.min(count, pool.length);
  const found = [];

  for (let k = 0; k < take; k += 1) {
    const index = closed
      ? Math.round((pool.length * k) / take) % pool.length
      : (take === 1 ? 0 : Math.round(((pool.length - 1) * k) / (take - 1)));

    found.push(...pool[index]);
  }

  guides.set(key, found);
  return found;
};

/**
 * Screen positions for a step's markers, in the order they should be clicked.
 * Returns null while the design isn't measurable yet, which is what tells the
 * overlay to keep the markers hidden rather than guess.
 */
export const resolveAnchors = (stepData, { search = false } = {}) => {
  if (stepData?.guideRungs) {
    return resolveGuideRungs(stepData, search);
  }

  if (stepData?.splitShapes) {
    return resolveSplitShapes(stepData, search);
  }

  if (stepData?.railPaths) {
    const spots = stepData.railPaths.map((spec) => projectRailPoint(spec, { search }));
    return spots.every(Boolean) ? spots : null;
  }

  if (stepData?.designPoints) {
    return reachableDesignPoints(stepData, search);
  }

  return null;
};
